import { billedPartyLabel, coopPartyNames } from "./consolidationBillingParty.js";
import { consolidationMillKey } from "./consolidationMillCalculation.js";
import { billableCalculationLabel } from "./billingCalculation.js";
import { breakComplete, sheetBreaks } from "./consolidationBreaks.js";

const overlapSeconds = (start, end, from, to) => from && to
  ? Math.max(0, (Math.min(Date.parse(end), Date.parse(to)) - Math.max(Date.parse(start), Date.parse(from))) / 1000) || 0 : 0;

// Lire les blocs et les parts du calcul existant, sans recalculer la facturation.
export function consolidationBreakdown(row, calculation, timing) {
  const source = calculation || timing;
  const names = coopPartyNames(row);
  const visits = new Map(row.visits.map(visit => [String(visit.id), visit]));
  const totals = new Map();
  for (const charge of calculation?.charges || []) {
    if (charge.billing_unit !== "heure") continue;
    const seconds = Number(charge.quantity) * 3600;
    if (!Number.isFinite(seconds)) continue;
    const key = consolidationMillKey(charge.mill);
    const total = totals.get(key) || { key, label: billedPartyLabel(charge.mill, names), seconds: 0, steps: new Map() };
    const visitId = charge.visit_id || charge.block?.visit_id;
    const stepKey = `${charge.kind}:${visitId || charge.key}`;
    const place = visits.get(String(visitId))?.label;
    const step = total.steps.get(stepKey) || { key: stepKey, kind: charge.kind, visitId: visitId ? String(visitId) : null,
      label: [billableCalculationLabel({ calculation_details: charge }), place].filter(Boolean).join(" · "),
      seconds: 0, sourceSeconds: Number(charge.source_seconds), coefficient: charge.coefficient };
    step.seconds += seconds;
    total.seconds += seconds;
    total.steps.set(stepKey, step);
    totals.set(key, total);
  }
  const recorded = sheetBreaks(row).filter(breakComplete);
  const blocks = (source?.blocks || []).flatMap(block => {
    const lunch = row.no_lunch ? 0 : overlapSeconds(block.start, block.end, row.lunch_start, row.lunch_end);
    // Chaque pause garde sa plage pour sa propre ligne de l'addition.
    const pauses = recorded.map(pause => ({ start: pause.start, end: pause.end, seconds: overlapSeconds(block.start, block.end, pause.start, pause.end) }))
      .filter(pause => pause.seconds > 0);
    const pause = pauses.reduce((sum, item) => sum + item.seconds, 0);
    if (!lunch && !pause) return [];
    const gross = Number(block.gross_seconds), net = Number(block.net_seconds);
    if (block.gross_seconds == null || block.net_seconds == null || !Number.isFinite(gross) || !Number.isFinite(net)) return [];
    const charges = (calculation?.charges || []).filter(charge => charge.billing_unit === "heure"
      && ["loading", "delivery"].includes(charge.kind)
      && String(charge.visit_id || charge.block?.visit_id) === String(block.visit_id));
    const parties = new Map();
    for (const charge of charges) {
      const key = consolidationMillKey(charge.mill);
      const party = parties.get(key) || { key, label: billedPartyLabel(charge.mill, names), seconds: 0 };
      party.seconds += Number(charge.quantity) * 3600;
      parties.set(key, party);
    }
    const other = Math.max(0, gross - net - lunch - pause);
    return [{ ...block, label: visits.get(String(block.visit_id))?.label || "Arrêt non renseigné",
      gross, net, lunch, pause, pauses, other, parties: [...parties.values()],
      matches: Math.abs(gross - lunch - pause - other - net) < 0.01 }];
  });
  return { official: Boolean(calculation && !calculation.partial), blocks,
    totals: [...totals.values()].map(total => ({ ...total, steps: [...total.steps.values()] })) };
}

// pause : une pause précise du relevé ; sans elle, tout le dîner ou toutes les pauses.
export function breakdownForEvent(breakdown, event, pause = null) {
  const blocks = breakdown.blocks.filter(block => pause ? block.pauses.some(item => item.start === pause.start && item.end === pause.end) : block[event] > 0);
  const partyKeys = new Set(blocks.flatMap(block => block.parties.map(party => party.key)));
  return { ...breakdown, blocks, totals: breakdown.official ? breakdown.totals.filter(total => partyKeys.has(total.key)) : [] };
}

const DEDUCTIONS = [["lunch", "Dîner"], ["pause", "Pause"], ["other", "Autre temps non facturé"]];
const SHARED = ["inspection", "return"];

// Arrondit chaque ligne à la minute pour que l’addition affichée tombe sur le total arrondi.
export function roundToTotal(lines, totalSeconds) {
  const exact = lines.map(line => line.seconds / 60), minutes = exact.map(Math.floor);
  let missing = Math.round(totalSeconds / 60) - minutes.reduce((sum, value) => sum + value, 0);
  [...exact.keys()].sort((a, b) => (exact[b] - minutes[b]) - (exact[a] - minutes[a]))
    .forEach(index => { if (missing-- > 0) minutes[index] += 1; });
  return lines.map((line, index) => ({ ...line, minutes: minutes[index] }));
}

// Une seule addition par coop : l’arrêt brut, le dîner et chaque pause retirés, puis les autres lignes.
export function billedLines(total, blocks) {
  const byVisit = new Map(blocks.map(block => [String(block.visit_id), block]));
  const lines = [], shared = total.steps.filter(step => SHARED.includes(step.kind));
  for (const step of total.steps.filter(step => !SHARED.includes(step.kind))) {
    const block = byVisit.get(step.visitId);
    if (!block?.net) { lines.push({ key: step.key, label: step.label, seconds: step.seconds }); continue; }
    const share = step.seconds / block.net;
    lines.push({ key: step.key, label: step.label, block, share, seconds: block.gross * share });
    for (const [field, label] of DEDUCTIONS) {
      if (field === "pause") for (const item of block.pauses || []) lines.push({ key: `${step.key}:pause:${item.start}`, label, field,
        range: [item.start, item.end], block, deduction: true, seconds: -item.seconds * share });
      else if (block[field] > 0) lines.push({ key: `${step.key}:${field}`, label, field, block, deduction: true, seconds: -block[field] * share });
    }
  }
  if (shared.length) {
    const ratios = shared.map(step => Number(step.coefficient?.numerator) / Number(step.coefficient?.denominator));
    const sameRatio = ratios.every(ratio => Number.isFinite(ratio) && Math.abs(ratio - ratios[0]) < 1e-9);
    lines.push({ key: "shared", label: shared.length > 1 ? "Inspection et retour" : shared[0].label,
      seconds: shared.reduce((sum, step) => sum + step.seconds, 0),
      percent: sameRatio ? ratios[0] * 100 : null,
      sourceSeconds: shared.reduce((sum, step) => sum + (Number(step.sourceSeconds) || 0), 0) });
  }
  return roundToTotal(lines, total.seconds);
}
